import React, { useState, useEffect, useMemo } from "react";
import "../../../css/ReleasedAppointmentTable.css";
import api from "../../../api/axiosConfig";

interface AppointmentCluster {
  id: string;
  center: string;
  type: string;
  city: string;
  screeningStartDate: string;
  screeningEndDate: string;
  totalSlots: number;
  total: number;
}

interface ReleasedSlot {
  _id: string;
  appointment_cluster_id: string;
  center_id: string;
  date: string;
  total_appointments: number;
}

interface CenterDetails {
  name: string;
  centerType: string;
  city: string;
}

interface Props {
  refreshTrigger?: number;
}

const ReleasedAppointmentsTable: React.FC<Props> = ({ refreshTrigger = 0 }) => {
  const [appointments, setAppointments] = useState<AppointmentCluster[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters and Pagination State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) {
          setError("Authentication token not found.");
          setLoading(false);
          return;
        }

        const releasedSlotsRes = await api.appointment_api.get("/api/released-slots", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const slots: ReleasedSlot[] = releasedSlotsRes.data.data || releasedSlotsRes.data || [];

        if (slots.length === 0) {
          setAppointments([]);
          setLoading(false);
          return;
        }

        // Group slots by appointment_cluster_id
        const clusterDataMap = new Map<string, {
          centerId: string;
          startDate: string;
          endDate: string;
          totalSlots: number;
          totalAppointments: number;
        }>();

        slots.forEach(slot => {
          const clusterId = slot.appointment_cluster_id;
          if (!clusterDataMap.has(clusterId)) {
            clusterDataMap.set(clusterId, {
              centerId: slot.center_id,
              startDate: slot.date,
              endDate: slot.date,
              totalSlots: 1,
              totalAppointments: slot.total_appointments || 0,
            });
          } else {
            const existing = clusterDataMap.get(clusterId)!;
            if (slot.date && existing.startDate && slot.date < existing.startDate) existing.startDate = slot.date;
            if (slot.date && existing.endDate && slot.date > existing.endDate) existing.endDate = slot.date;
            existing.totalSlots += 1;
            existing.totalAppointments += (slot.total_appointments || 0);
          }
        });

        const uniqueCenterIds = Array.from(new Set(Array.from(clusterDataMap.values()).map(c => c.centerId)));

        const centerDetailPromises = uniqueCenterIds.map(id =>
          api.center_api.get<{ success: boolean, data: CenterDetails }>(`/api/v1/centers/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );

        const centerResponses = await Promise.all(centerDetailPromises);

        const centerDataMap = new Map<string, CenterDetails>();
        centerResponses.forEach((res, i) => {
          if (res.data?.success) {
            centerDataMap.set(uniqueCenterIds[i], res.data.data);
          }
        });

        const finalAppointmentData = Array.from(clusterDataMap.entries()).map(([clusterId, clusterData]) => {
          const centerItem = centerDataMap.get(clusterData.centerId);

          return {
            id: clusterId,
            screeningStartDate: clusterData.startDate,
            screeningEndDate: clusterData.endDate,
            totalSlots: clusterData.totalSlots,
            total: clusterData.totalAppointments,
            center: centerItem?.name || "Unknown",
            type: centerItem?.centerType || "Unknown",
            city: centerItem?.city || "Unknown",
          };
        });

        setAppointments(finalAppointmentData);

      } catch (err) {
        console.error("Failed to fetch appointment details:", err);
        setError("Could not load appointment data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentDetails();
  }, [refreshTrigger]);

  // Derive unique categories and cities for dropdown options
  const uniqueCategories = useMemo(() => {
    const categories = appointments
      .map((a) => a.type)
      .filter((type): type is string => Boolean(type && type !== "Unknown"));
    return Array.from(new Set(categories));
  }, [appointments]);

  const uniqueCities = useMemo(() => {
    const cities = appointments
      .map((a) => a.city)
      .filter((city): city is string => Boolean(city && city !== "Unknown"));
    return Array.from(new Set(cities));
  }, [appointments]);

  // Filtering logic by search term, category, and city
  const filteredAppointments = useMemo(() => {
    return appointments.filter((a) => {
      const matchesSearch =
        !searchTerm ||
        a.center.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.city.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        !selectedCategory || a.type === selectedCategory;

      const matchesCity =
        !selectedCity || a.city === selectedCity;

      return matchesSearch && matchesCategory && matchesCity;
    });
  }, [appointments, searchTerm, selectedCategory, selectedCity]);

  // Reset page to 1 on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedCity]);

  // Calculate pagination variables
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const currentAppointmentsToDisplay = useMemo(() => {
    return filteredAppointments.slice(startIndex, endIndex);
  }, [filteredAppointments, startIndex, endIndex]);

  if (loading) {
    return <div className="released-table"><p>Loading appointments...</p></div>;
  }
  if (error) {
    return <div className="released-table"><p style={{ color: "red" }}>{error}</p></div>;
  }

  return (
    <div className="released-table">
      <div className="release-apt-table-header">
        <h3>List of Clusters Created</h3>
        
        {/* Filter Controls: Center Category, City, Search */}
        <div className="filter-controls-bar">
          <select
            className="center-filter-dropdown"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Center Category</option>
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          <select
            className="center-filter-dropdown"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
          >
            <option value="">City</option>
            {uniqueCities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search by Center Name"
            className="search-box"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Center Name</th>
            <th>Center Type</th>
            <th>City</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Slots</th>
            <th>Appointments</th>
          </tr>
        </thead>
        <tbody>
          {currentAppointmentsToDisplay.length > 0 ? (
            currentAppointmentsToDisplay.map((a, i) => (
              <tr key={a.id}>
                <td>{startIndex + i + 1}</td>
                <td>{a.center}</td>
                <td><span className="tag">{a.type}</span></td>
                <td>{a.city}</td>
                <td>{a.screeningStartDate}</td>
                <td>{a.screeningEndDate}</td>
                <td>{a.totalSlots}</td>
                <td>{a.total}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center' }}>No released appointments match your search/filters.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Dynamic pagination controls */}
      {totalPages > 0 && (
        <div className="released-table-pagination-footer">
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredAppointments.length)} of {filteredAppointments.length} results
          </p>
          <div className="released-table-pagination">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(pageNumber => (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={currentPage === pageNumber ? "active" : ""}
              >
                {pageNumber}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleasedAppointmentsTable;